import { endOfDay, format, startOfDay, subDays, subHours } from 'date-fns'
import { makeAutoObservable } from 'mobx'
import { GraphData } from '../components/LineGraph'
import Events, { ManagedNewSubscriptionEvent, ManagedPaymentEvent, ManagedSubscriptionCancelledEvent, ManagedSubscriptionExpiredEvent } from './Events'
import Treasury from './Treasury'
import Radom, { CheckoutFunnelSummaryRow } from './Radom'
import { testNetworks, prodNetworks } from '../util/Managed'
import User from './User'

class Analytics {
  isLoading: boolean = false

  constructor() {
    makeAutoObservable(this)
  }

  private readonly getDayBuckets = (days: number): GraphData => {
    const start = startOfDay(subDays(new Date(), days - 1))
    return Array(days).fill(0).map((_, idx) => {
      const d = new Date(start)
      d.setDate(start.getDate() + idx)
      d.setHours(12, 0, 0, 0)
      return {
        x: d.getTime(),
        y: 0
      }
    })
  }

  private readonly isRevenuePaymentEvent = (e: any): e is ManagedPaymentEvent => {
    return e.name === 'PaymentEvent' ||
      e.name === 'SubscriptionPaymentEvent' ||
      e.name === 'RecurringPaymentEvent' ||
      e.name === 'OpenBanking'
  }

  private readonly getConversionRate = (from: string, event?: ManagedPaymentEvent): number | undefined => {
    const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'

    if (from.toLowerCase() === defaultCurrency.toLowerCase()) {
      return 1
    }

    const priceQuote = Treasury.priceQuotes.find(p => p.from.toLowerCase() === from.toLowerCase())
    if (priceQuote) {
      return priceQuote.value
    }

    const eventConversion = event?.conversionRates?.find(p =>
      p.from.toLowerCase() === from.toLowerCase() &&
      p.to.toLowerCase() === defaultCurrency.toLowerCase()
    )
    return eventConversion ? Number(eventConversion.rate) : undefined
  }

  private readonly getManagedPaymentValue = (
    event: ManagedPaymentEvent,
    amountType: 'gross' | 'net' = 'gross'
  ): number => {
    const summary = event.paymentSummary
    const summaryAmount = amountType === 'net' ? summary?.netAmount : summary?.grossAmount
    if (summaryAmount && summary?.currency) {
      const summaryRate = this.getConversionRate(summary.currency, event)
      if (summaryRate !== undefined) {
        return Number(summaryAmount) * summaryRate
      }
    }

    const conversion = this.getConversionRate(event.paymentMethod.ticker, event) ?? 1
    return Number(event.amount) * conversion
  }

  private readonly getCheckoutFunnelSeries = async (
    days: number,
    rail: 'crypto' | 'openBanking',
    metric: 'started' | 'completed' | 'conversion'
  ): Promise<GraphData> => {
    const data = this.getDayBuckets(days)
    if (data.length === 0) {
      return data
    }

    const from = startOfDay(new Date(data[0].x)).toISOString()
    const to = endOfDay(new Date(data[data.length - 1].x)).toISOString()

    let rows: CheckoutFunnelSummaryRow[] = []
    try {
      rows = await Radom.getCheckoutFunnelSummary(from, to)
    } catch (err) {
      console.error('Failed to load checkout funnel summary', err)
      return data
    }

    const rowsByRail = rows.filter(row => row.rail === rail)
    const buckets = rowsByRail.reduce<Record<string, { started: number, completed: number }>>((acc, row) => {
      const current = acc[row.bucketDate] ?? { started: 0, completed: 0 }
      current.started += row.startedSessions
      current.completed += row.completedSessions
      acc[row.bucketDate] = current
      return acc
    }, {})

    return data.map((point) => {
      const bucketDate = format(new Date(point.x), 'yyyy-MM-dd')
      const totals = buckets[bucketDate]
      if (!totals) {
        return point
      }

      if (metric === 'started') {
        return { ...point, y: totals.started }
      }

      if (metric === 'completed') {
        return { ...point, y: totals.completed }
      }

      return {
        ...point,
        y: totals.started > 0 ? (totals.completed / totals.started) * 100 : 0
      }
    })
  }

  getGrossDailyVolume = async (periodOffset: number = 0, testMode: boolean): Promise<GraphData> => {
    const end = subHours(new Date(), periodOffset * 24)

    const data = Array(25).fill(0).map((_, idx) => ({
      x: subHours(end, 24 - idx).getTime(),
      y: 0
    }))

    Events.managedEvents.filter(this.isRevenuePaymentEvent).forEach((e: ManagedPaymentEvent) => {
      if ((e.paymentMethod.isTestnet && !testMode) || (!e.paymentMethod.isTestnet && testMode)) {
        return
      }

      const eventTime = new Date(e.date).getTime()
      if (eventTime < data[0].x || eventTime > data[data.length - 1].x) return

      for (let i = 0; i < data.length; i++) {
        if (eventTime > data[i].x) {
          continue
        }

        data[i].y += this.getManagedPaymentValue(e)
        break
      }
    })

    return data
  }

  getManagedGrossVolume = (days: number, testMode: boolean): GraphData => {
    const end = endOfDay(new Date())

    const data = Array(days).fill(0).map((_, idx) => ({
      x: subDays(end, idx).getTime(),
      y: 0
    })).reverse()

    Events.managedEvents.filter(this.isRevenuePaymentEvent).forEach((e: ManagedPaymentEvent) => {
      if ((e.paymentMethod.isTestnet && !testMode) || (!e.paymentMethod.isTestnet && testMode)) {
        return
      }

      const eventTime = new Date(e.date).getTime()
      if (eventTime < data[0].x) return

      for (let i = 0; i < data.length; i++) {
        if (eventTime > data[i].x) {
          continue
        }
        data[i].y += this.getManagedPaymentValue(e)
        break
      }
    })

    return data
  }

  getGrossVolume = async(days: number, testMode: boolean): Promise<GraphData> => {
    const managedVolume = this.getManagedGrossVolume(days, testMode)
    return managedVolume
  }

  getNetVolume = async (days: number, testMode: boolean): Promise<GraphData> => {
    const end = endOfDay(new Date())

    const data = Array(days).fill(0).map((_, idx) => ({
      x: subDays(end, idx).getTime(),
      y: 0
    })).reverse()

    Events.managedEvents.filter(this.isRevenuePaymentEvent).forEach((e: ManagedPaymentEvent) => {
      if ((e.paymentMethod.isTestnet && !testMode) || (!e.paymentMethod.isTestnet && testMode)) {
        return
      }

      const eventTime = new Date(e.date).getTime()
      if (eventTime < data[0].x) return

      for (let i = 0; i < data.length; i++) {
        if (eventTime > data[i].x) {
          continue
        }
        data[i].y += this.getManagedPaymentValue(e, 'net')
        break
      }
    })

    return data
  }

  getRecurringPayments = async (days: number, testMode: boolean): Promise<GraphData> => {
    const end = endOfDay(new Date())

    const data = Array(days).fill(0).map((_, idx) => ({
      x: subDays(end, idx).getTime(),
      y: 0
    })).reverse()

    Events.managedEvents.filter(e => e.name === 'RecurringPaymentEvent' || e.name === 'SubscriptionPaymentEvent').forEach((e: ManagedPaymentEvent) => {
      if ((e.paymentMethod.isTestnet && !testMode) || (!e.paymentMethod.isTestnet && testMode)) {
        return
      }

      const eventTime = new Date(e.date).getTime()
      if (eventTime < data[0].x) return

      for (let i = 0; i < data.length; i++) {
        if (eventTime > data[i].x) {
          continue
        }

        data[i].y += 1
        break
      }
    })

    return data
  }

  getOneTimePayments = async (days: number, testMode: boolean): Promise<GraphData> => {
    const end = endOfDay(new Date())

    const data = Array(days).fill(0).map((_, idx) => ({
      x: subDays(end, idx).getTime(),
      y: 0
    })).reverse()

    Events.managedEvents.filter(e => e.name === 'PaymentEvent' || e.name === 'OpenBanking').forEach((e: ManagedPaymentEvent) => {
      if ((e.paymentMethod.isTestnet && !testMode) || (!e.paymentMethod.isTestnet && testMode)) {
        return
      }

      const eventTime = new Date(e.date).getTime()
      if (eventTime < data[0].x) return

      for (let i = 0; i < data.length; i++) {
        if (eventTime > data[i].x) {
          continue
        }
        data[i].y += 1
        break
      }
    })

    return data
  }

  getAveragePaymentSize = async (days: number, testMode: boolean): Promise<GraphData> => {
    const end = endOfDay(new Date())

    const totals = Array(days).fill(0).map((_, idx) => ({
      x: subDays(end, idx).getTime(),
      total: 0,
      count: 0
    })).reverse()

    Events.managedEvents.filter(this.isRevenuePaymentEvent).forEach((e: ManagedPaymentEvent) => {
      if ((e.paymentMethod.isTestnet && !testMode) || (!e.paymentMethod.isTestnet && testMode)) {
        return
      }

      const eventTime = new Date(e.date).getTime()
      if (eventTime < totals[0].x) return

      for (let i = 0; i < totals.length; i++) {
        if (eventTime > totals[i].x) {
          continue
        }
        totals[i].total += this.getManagedPaymentValue(e)
        totals[i].count += 1
        break
      }
    })

    return totals.map(point => ({
      x: point.x,
      y: point.count > 0 ? point.total / point.count : 0
    }))
  }

  getNewSubscriptions = async (days: number, testMode: boolean): Promise<GraphData> => {
    const end = endOfDay(new Date())

    const data = Array(days).fill(0).map((_, idx) => ({
      x: subDays(end, idx).getTime(),
      y: 0
    })).reverse()

    Events.managedEvents.filter(e => e.name === 'NewSubscription').forEach((e: ManagedNewSubscriptionEvent) => {
      if (e.managedMethod.isTestnet && !testMode) {
        return
      }

      const eventTime = e.date.getTime()
      if (eventTime < data[0].x) return

      for (let i = 0; i < data.length; i++) {
        if (eventTime > data[i].x) {
          continue
        }
        data[i].y += 1
        break
      }
    })

    return data
  }

  getCancelledSubscriptions = async (days: number, testMode: boolean): Promise<GraphData> => {
    const end = endOfDay(new Date())

    const data = Array(days).fill(0).map((_, idx) => ({
      x: subDays(end, idx).getTime(),
      y: 0
    })).reverse()

    Events.allEvents.filter(e => e.name === 'SubscriptionCancelled').forEach((s: ManagedSubscriptionCancelledEvent) => {
      if (s.managedMethod.isTestnet && !testMode) {
        return
      }

      const subscriptionCancelled = new Date(s.data.cancelledAt).getTime()
      if (subscriptionCancelled < data[0].x) return

      for (let i = 0; i < data.length; i++) {
        if (subscriptionCancelled > data[i].x) {
          continue
        }
        data[i].y += 1
        return
      }
    })

    return data
  }

  getSubscriptionChurnRate = async (days: number, testMode: boolean): Promise<GraphData> => {
    const [subscribers, cancelled, expired] = await Promise.all([
      this.getTotalSubscribers(days, testMode),
      this.getCancelledSubscriptions(days, testMode),
      this.getExpiredSubscriptions(days, testMode)
    ])

    return subscribers.map((point, idx) => {
      const lostSubscriptions = (cancelled[idx]?.y ?? 0) + (expired[idx]?.y ?? 0)
      const subscriberBase = idx === 0
        ? point.y + lostSubscriptions
        : subscribers[idx - 1].y

      return {
        x: point.x,
        y: subscriberBase > 0 ? (lostSubscriptions / subscriberBase) * 100 : 0
      }
    })
  }

  getExpiredSubscriptions = async (days: number, testMode: boolean): Promise<GraphData> => {
    const end = endOfDay(new Date())

    const data = Array(days).fill(0).map((_, idx) => ({
      x: subDays(end, idx).getTime(),
      y: 0
    })).reverse()

    Events.allEvents.filter(e => e.name === 'SubscriptionExpired').forEach((s: ManagedSubscriptionExpiredEvent) => {
      if (s.managedMethod.isTestnet && !testMode) {
        return
      }

      const subscriptionExpired = new Date(s.data.expiredAt).getTime()
      if (subscriptionExpired < data[0].x) return

      for (let i = 0; i < data.length; i++) {
        if (subscriptionExpired > data[i].x) {
          continue
        }
        data[i].y += 1
        return
      }
    })

    return data
  }

  getCryptoCheckoutConversionRate = async (days: number, _testMode: boolean): Promise<GraphData> => {
    return await this.getCheckoutFunnelSeries(days, 'crypto', 'conversion')
  }

  getOpenBankingCheckoutConversionRate = async (days: number, _testMode: boolean): Promise<GraphData> => {
    return await this.getCheckoutFunnelSeries(days, 'openBanking', 'conversion')
  }

  getCryptoCheckoutStarted = async (days: number, _testMode: boolean): Promise<GraphData> => {
    return await this.getCheckoutFunnelSeries(days, 'crypto', 'started')
  }

  getOpenBankingCheckoutStarted = async (days: number, _testMode: boolean): Promise<GraphData> => {
    return await this.getCheckoutFunnelSeries(days, 'openBanking', 'started')
  }

  getCryptoCheckoutCompleted = async (days: number, _testMode: boolean): Promise<GraphData> => {
    return await this.getCheckoutFunnelSeries(days, 'crypto', 'completed')
  }

  getOpenBankingCheckoutCompleted = async (days: number, _testMode: boolean): Promise<GraphData> => {
    return await this.getCheckoutFunnelSeries(days, 'openBanking', 'completed')
  }

  getTotalSubscribers = async (days: number, testMode: boolean): Promise<GraphData> => {
    const end = endOfDay(new Date())

    const data = Array(days).fill(0).map((_, idx) => ({
      x: subDays(end, idx).getTime(),
      y: 0
    })).reverse()

    const subscriptions = await Radom.listSubscriptions(
      new Date(data[0].x).toISOString(),
      new Date(data[0].x).toISOString(),
      new Date(data[0].x).toISOString(),
      testMode ? testNetworks : prodNetworks,
      ['active', 'cancelled', 'expired'],
      0,
      100
    )

    const newSubscriptions = await this.getNewSubscriptions(days, testMode)
    const expiredSubscriptions = await this.getExpiredSubscriptions(days, testMode)
    const cancelledSubscriptions = await this.getCancelledSubscriptions(days, testMode)

    for (let i = 0; i < data.length; i++) {
      const baseAmount = i === 0 ? subscriptions.total : data[i - 1].y
      data[i].y = baseAmount + newSubscriptions[i].y - expiredSubscriptions[i].y - cancelledSubscriptions[i].y
    }

    return data
  }
}

export default new Analytics()
